import { useState } from 'react';
import { Group, SimpleGrid, Stack, Table, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';
import {
  lineOf,
  type BreakdownKey,
  type MonthRevenue,
  type MonumentTerms,
  type RevenueOverview,
} from 'src/secret-menu/billing/hooks/useRevenue';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import SectionCard from '../overview/SectionCard';
import FirmsTable from './FirmsTable';
import RevenueHistoryChart from './RevenueHistoryChart';
import { BilledUnitsStat, FullAdoptionStat, MrrStat } from './RevenueStats';
import RevenueStepsChart from './RevenueStepsChart';
import { Stat, formatRevenue } from './ui';

type Props = {
  overview: RevenueOverview;
  history: readonly MonthRevenue[];
  monument: MonumentTerms;
  firms: readonly BillingDetail[];
  today: Date;
  onEditFirm: (companyId: string) => void;
};

type SourceRow = {
  key: BreakdownKey;
  label: string;
  note?: string;
  indent?: boolean;
  started?: boolean;
};

export default function RevenuePanel({
  overview,
  history,
  monument,
  firms,
  today,
  onEditFirm,
}: Props) {
  const [monumentOpen, setMonumentOpen] = useState(false);
  const notStarted = (pence: number) =>
    `Not started · ${formatRevenue(pence)} a month once it does`;
  const customers = monument.customerCount;

  const adviserRows: SourceRow[] = [
    { key: 'ADVISER_PER_UNIT', label: 'Adviser firms · per family' },
    { key: 'ADVISER_FIXED', label: 'Adviser firms · fixed fee' },
  ];
  const monumentRows: SourceRow[] = [
    {
      key: 'MONUMENT_BUILD',
      label: 'Strategic partner build fee',
      note: monument.buildFeeStarted ? 'Started' : notStarted(monument.buildFeePence),
      started: monument.buildFeeStarted,
      indent: true,
    },
    {
      key: 'MONUMENT_LICENSE',
      label: 'Strategic partner license fee',
      note:
        customers > 0
          ? `${customers.toLocaleString('en-GB')} ${customers === 1 ? 'customer' : 'customers'}`
          : 'Starts with the first customer',
      started: customers > 0,
      indent: true,
    },
  ];

  const amountCells = (mrrPence: number, arrPence: number, dimmed = false) => (
    <>
      <Table.Td ta="right" c={dimmed ? 'dimmed' : undefined}>
        {formatRevenue(mrrPence)}
      </Table.Td>
      <Table.Td ta="right" c={dimmed ? 'dimmed' : undefined}>
        {formatRevenue(arrPence)}
      </Table.Td>
    </>
  );

  const sourceRow = (row: SourceRow) => {
    const line = lineOf(overview.today, row.key);
    const dimmed = row.indent === true && !row.started;
    return (
      <Table.Tr key={row.key}>
        <Table.Td pl={row.indent ? 'xl' : undefined}>
          <Text size="textSm" c={dimmed ? 'dimmed' : undefined}>
            {row.label}
          </Text>
          {row.note && (
            <Text size="textXs" c="dimmed">
              {row.note}
            </Text>
          )}
        </Table.Td>
        {amountCells(line.mrrPence, line.arrPence, dimmed)}
      </Table.Tr>
    );
  };

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <MrrStat overview={overview} history={history} today={today} />
        <BilledUnitsStat overview={overview} history={history} today={today} />
        <FullAdoptionStat overview={overview} />
        <Stat
          label="Pipeline (free trials)"
          value={formatRevenue(overview.contractedNotBillingPence)}
          detail={`a month if they convert · ${formatRevenue(overview.pipelineFullAdoptionPence)} at full adoption`}
          hint="pipeline"
        />
      </SimpleGrid>

      <RevenueHistoryChart history={history} today={today} />

      <SectionCard title="Revenue (actual)">
        <Table verticalSpacing={6} style={{ fontVariantNumeric: 'tabular-nums' }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              <Table.Th ta="right">Monthly</Table.Th>
              <Table.Th ta="right">Yearly</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {adviserRows.map(sourceRow)}
            <Table.Tr onClick={() => setMonumentOpen(open => !open)} style={{ cursor: 'pointer' }}>
              <Table.Td>
                <Group gap={6} wrap="nowrap">
                  <UnstyledButton
                    aria-expanded={monumentOpen}
                    aria-label={`${monumentOpen ? 'Hide' : 'Show'} strategic partner fees`}
                    style={{ display: 'flex' }}
                  >
                    {monumentOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                  </UnstyledButton>
                  <Text size="textSm">Strategic partner</Text>
                </Group>
              </Table.Td>
              {amountCells(
                overview.monumentToday.totalPence,
                monumentRows.reduce(
                  (total, row) => total + lineOf(overview.today, row.key).arrPence,
                  0,
                ),
              )}
            </Table.Tr>
            {monumentOpen && monumentRows.map(sourceRow)}
            <Table.Tr
              style={{
                fontWeight: FONT_WEIGHTS.semibold,
                background: 'var(--mantine-color-default-hover)',
              }}
            >
              <Table.Td>Total</Table.Td>
              {amountCells(overview.today.mrrPence, overview.today.arrPence)}
            </Table.Tr>
          </Table.Tbody>
        </Table>
        {overview.contractedNotBillingPence > 0 && (
          <Text size="textXs" c="dimmed" mt="xs">
            Not counted yet: {formatRevenue(overview.contractedNotBillingPence)} a month agreed with
            firms still in trial.
          </Text>
        )}
      </SectionCard>

      <RevenueStepsChart steps={overview.steps} />

      <FirmsTable firms={firms} onEditFirm={onEditFirm} />
    </Stack>
  );
}
